import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import MultiEntryField from '@/components/MultiEntryField';
import AffiliationField from '@/components/AffiliationField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  User, 
  Briefcase, 
  Building2, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Camera,
  Trash2,
  Palette,
  Save,
  ArrowLeft,
  Linkedin,
  Twitter,
  Instagram,
  Github,
  Facebook,
  QrCode,
  Download,
  Copy,
  Smartphone,
  ExternalLink,
  Eye,
  ShoppingCart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

const SOCIAL_PLATFORMS = [
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/username' },
  { id: 'twitter', label: 'Twitter', icon: Twitter, placeholder: 'https://twitter.com/username' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username' },
  { id: 'github', label: 'GitHub', icon: Github, placeholder: 'https://github.com/username' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/username' },
];

const EditCardPage = () => {
  const { cardId } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nfcData, setNfcData] = useState(null);
  const [withLogo, setWithLogo] = useState(false);
  const [qrWithLogoUrl, setQrWithLogoUrl] = useState(null);
  const [generatingQR, setGeneratingQR] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    bio: '',
    profile_photo: '',
    qr_color: '#2563EB',
    qr_bg_color: '#FFFFFF',
    social_links: [],
    // Affiliations (new structure)
    affiliations: [],
    // Personal contact info
    personal_emails: [],
    personal_phones: [],
    personal_websites: [],
    personal_addresses: [],
    // Legacy multi-entry fields (for backward compatibility)
    job_titles: [],
    emails: [],
    phones: [],
    websites: [],
    addresses: []
  });

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cards/${cardId}`, { headers: getAuthHeaders() });
        // Ensure all fields have default values
        const cardData = {
          ...response.data,
          affiliations: response.data.affiliations || [],
          personal_emails: response.data.personal_emails || [],
          personal_phones: response.data.personal_phones || [],
          personal_websites: response.data.personal_websites || [],
          personal_addresses: response.data.personal_addresses || [],
          job_titles: response.data.job_titles || [],
          emails: response.data.emails || [],
          phones: response.data.phones || [],
          websites: response.data.websites || [],
          addresses: response.data.addresses || [],
        };
        setFormData(cardData);
        
        // Fetch NFC data
        const nfcRes = await axios.get(`${API_URL}/api/cards/${cardId}/nfc-data`, { headers: getAuthHeaders() });
        setNfcData(nfcRes.data);
      } catch (error) {
        toast.error('Card not found');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [cardId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload/photo`, formDataUpload, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      handleChange('profile_photo', response.data.url);
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const addSocialLink = (platform) => {
    const existing = formData.social_links.find(l => l.platform === platform);
    if (existing) return;
    setFormData(prev => ({
      ...prev,
      social_links: [...prev.social_links, { platform, url: '' }]
    }));
  };

  const updateSocialLink = (platform, url) => {
    setFormData(prev => ({
      ...prev,
      social_links: prev.social_links.map(link => 
        link.platform === platform ? { ...link, url } : link
      )
    }));
  };

  const removeSocialLink = (platform) => {
    setFormData(prev => ({
      ...prev,
      social_links: prev.social_links.filter(link => link.platform !== platform)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/cards/${cardId}`, formData, { headers: getAuthHeaders() });
      toast.success('Card updated successfully!');
    } catch (error) {
      toast.error('Failed to update card');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${FRONTEND_URL}/card/${cardId}`);
    toast.success('Link copied to clipboard');
  };

  const generateQRWithLogo = async () => {
    if (!formData.profile_photo) {
      toast.error('Please upload a profile photo first to use as logo');
      return;
    }
    
    setGeneratingQR(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/cards/${cardId}/qr-base64?with_logo=true&color=${encodeURIComponent(formData.qr_color)}&bg_color=${encodeURIComponent(formData.qr_bg_color)}&size=400`,
        { headers: getAuthHeaders() }
      );
      setQrWithLogoUrl(response.data.qr_code);
      toast.success('QR code with logo generated!');
    } catch (error) {
      toast.error('Failed to generate QR with logo');
    } finally {
      setGeneratingQR(false);
    }
  };

  const downloadQR = () => {
    // If we have a QR with logo, download that
    if (withLogo && qrWithLogoUrl) {
      const link = document.createElement('a');
      link.download = `${formData.name.replace(/\s+/g, '_')}_QR_with_logo.png`;
      link.href = qrWithLogoUrl;
      link.click();
      return;
    }
    
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = formData.qr_bg_color;
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      
      const link = document.createElement('a');
      link.download = `${formData.name.replace(/\s+/g, '_')}_QR.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handleLogoToggle = (checked) => {
    setWithLogo(checked);
    if (checked) {
      generateQRWithLogo();
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  const cardUrl = `${FRONTEND_URL}/card/${cardId}`;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">Edit Card</h1>
            <p className="text-muted-foreground">Update your digital business card details</p>
          </div>
          <a href={`/card/${cardId}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="gap-2" data-testid="preview-card-btn">
              <ExternalLink className="w-4 h-4" />
              Preview
            </Button>
          </a>
        </div>

        <Tabs defaultValue="details" className="space-y-6">
          <TabsList>
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="qr" data-testid="tab-qr">QR Code</TabsTrigger>
            <TabsTrigger value="nfc" data-testid="tab-nfc">NFC</TabsTrigger>
            <TabsTrigger value="print" data-testid="tab-print">Print Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <form onSubmit={handleSubmit}>
              <div className="split-view">
                {/* Form Section */}
                <div className="space-y-6">
                  {/* Profile Photo */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Camera className="w-5 h-5" />
                        Profile Photo
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-6">
                        <div 
                          className="photo-upload"
                          onClick={() => fileInputRef.current?.click()}
                          data-testid="photo-upload-btn"
                        >
                          {formData.profile_photo ? (
                            <img src={formData.profile_photo} alt="Profile" />
                          ) : (
                            <Camera className="w-8 h-8 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium">Upload a photo</p>
                          <p className="text-xs text-muted-foreground">JPG, PNG or WebP. Max 5MB.</p>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Basic Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Basic Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          data-testid="name-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="bio">Bio</Label>
                        <Textarea
                          id="bio"
                          placeholder="A short bio about yourself..."
                          rows={3}
                          value={formData.bio || ''}
                          onChange={(e) => handleChange('bio', e.target.value)}
                          data-testid="bio-input"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Company Affiliations */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Company Affiliations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <AffiliationField
                        affiliations={formData.affiliations || []}
                        onChange={(affiliations) => handleChange('affiliations', affiliations)}
                        maxAffiliations={5}
                      />
                    </CardContent>
                  </Card>

                  {/* Personal Contact Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Personal Contact Information
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Contact details not tied to any specific company
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Personal Emails */}
                      <MultiEntryField
                        field="personal_emails"
                        entries={formData.personal_emails || []}
                        onChange={(entries) => handleChange('personal_emails', entries)}
                        icon={Mail}
                        placeholder="personal@email.com"
                        inputType="email"
                        label="Personal Email Addresses"
                      />

                      <Separator />

                      {/* Personal Phones */}
                      <MultiEntryField
                        field="personal_phones"
                        entries={formData.personal_phones || []}
                        onChange={(entries) => handleChange('personal_phones', entries)}
                        icon={Phone}
                        placeholder="+1 234 567 8900"
                        inputType="tel"
                        label="Personal Phone Numbers"
                      />

                      <Separator />

                      {/* Personal Websites */}
                      <MultiEntryField
                        field="personal_websites"
                        entries={formData.personal_websites || []}
                        onChange={(entries) => handleChange('personal_websites', entries)}
                        icon={Globe}
                        placeholder="https://yourportfolio.com"
                        inputType="url"
                        label="Personal Websites"
                      />

                      <Separator />

                      {/* Personal Addresses */}
                      <MultiEntryField
                        field="personal_addresses"
                        entries={formData.personal_addresses || []}
                        onChange={(entries) => handleChange('personal_addresses', entries)}
                        icon={MapPin}
                        placeholder="Home address"
                        isTextarea={true}
                        label="Personal Addresses"
                      />
                    </CardContent>
                  </Card>

                  {/* Social Links */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Social Links</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {formData.social_links.map((link) => {
                        const platform = SOCIAL_PLATFORMS.find(p => p.id === link.platform);
                        const Icon = platform?.icon || Globe;
                        return (
                          <div key={link.platform} className="flex items-center gap-2">
                            <Icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                            <Input
                              placeholder={platform?.placeholder}
                              value={link.url}
                              onChange={(e) => updateSocialLink(link.platform, e.target.value)}
                              data-testid={`social-${link.platform}-input`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeSocialLink(link.platform)}
                              data-testid={`remove-social-${link.platform}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        );
                      })}
                      
                      <Separator />
                      
                      <div className="flex flex-wrap gap-2">
                        {SOCIAL_PLATFORMS.filter(p => !formData.social_links.find(l => l.platform === p.id)).map((platform) => {
                          const Icon = platform.icon;
                          return (
                            <Button
                              key={platform.id}
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addSocialLink(platform.id)}
                              data-testid={`add-social-${platform.id}`}
                            >
                              <Icon className="w-4 h-4 mr-2" />
                              {platform.label}
                            </Button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* QR Customization */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        QR Code Customization
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="qr_color">QR Color</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              id="qr_color"
                              value={formData.qr_color}
                              onChange={(e) => handleChange('qr_color', e.target.value)}
                              className="color-swatch"
                              data-testid="qr-color-input"
                            />
                            <Input
                              value={formData.qr_color}
                              onChange={(e) => handleChange('qr_color', e.target.value)}
                              className="w-24 font-mono text-sm"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="qr_bg_color">Background</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              id="qr_bg_color"
                              value={formData.qr_bg_color}
                              onChange={(e) => handleChange('qr_bg_color', e.target.value)}
                              className="color-swatch"
                              data-testid="qr-bg-color-input"
                            />
                            <Input
                              value={formData.qr_bg_color}
                              onChange={(e) => handleChange('qr_bg_color', e.target.value)}
                              className="w-24 font-mono text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full btn-glow gap-2"
                    disabled={saving}
                    data-testid="save-card-btn"
                  >
                    {saving ? (
                      <div className="loading-spinner w-5 h-5" />
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>

                {/* Preview Section */}
                <div className="hidden lg:block sticky top-8">
                  <Card className="overflow-hidden">
                    <CardHeader className="bg-muted/50">
                      <CardTitle className="text-lg">Live Preview</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="mobile-preview-container mx-auto overflow-y-auto">
                        <div className="p-6 space-y-6">
                          <div className="text-center pt-8">
                            {formData.profile_photo ? (
                              <img 
                                src={formData.profile_photo} 
                                alt="Profile"
                                className="w-24 h-24 rounded-full mx-auto object-cover ring-4 ring-primary/20"
                              />
                            ) : (
                              <div className="w-24 h-24 rounded-full mx-auto bg-primary/10 flex items-center justify-center ring-4 ring-primary/20">
                                <span className="text-3xl font-bold text-primary">
                                  {formData.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                            <h2 className="text-xl font-bold mt-4">{formData.name || 'Your Name'}</h2>
                            {formData.job_titles?.[0]?.value && (
                              <p className="text-muted-foreground">{formData.job_titles[0].value}</p>
                            )}
                            {formData.company && (
                              <p className="text-sm text-muted-foreground">{formData.company}</p>
                            )}
                            {formData.job_titles?.length > 1 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                +{formData.job_titles.length - 1} more title(s)
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            {formData.emails?.[0]?.value && (
                              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                                <Mail className="w-5 h-5 text-primary" />
                                <span className="text-sm truncate">{formData.emails[0].value}</span>
                                {formData.emails.length > 1 && (
                                  <span className="text-xs text-muted-foreground">+{formData.emails.length - 1}</span>
                                )}
                              </div>
                            )}
                            {formData.phones?.[0]?.value && (
                              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                                <Phone className="w-5 h-5 text-primary" />
                                <span className="text-sm">{formData.phones[0].value}</span>
                                {formData.phones.length > 1 && (
                                  <span className="text-xs text-muted-foreground">+{formData.phones.length - 1}</span>
                                )}
                              </div>
                            )}
                            {formData.websites?.[0]?.value && (
                              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                                <Globe className="w-5 h-5 text-primary" />
                                <span className="text-sm truncate">{formData.websites[0].value}</span>
                                {formData.websites.length > 1 && (
                                  <span className="text-xs text-muted-foreground">+{formData.websites.length - 1}</span>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex justify-center">
                            <div className="qr-container">
                              <QRCodeSVG
                                value={cardUrl}
                                size={120}
                                fgColor={formData.qr_color}
                                bgColor={formData.qr_bg_color}
                                level="H"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="qr">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5" />
                    Your QR Code
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-6">
                  {/* Advanced Customizer Link */}
                  <div className="w-full p-4 bg-gradient-to-r from-[#16335E]/10 to-[#00A78B]/10 rounded-lg border border-[#16335E]/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-sm">Want more customization?</h4>
                        <p className="text-xs text-muted-foreground">Customize size, shape, colors, and download HD</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => navigate(`/cards/${cardId}/qr-customizer`)}
                        className="gap-1"
                        data-testid="advanced-qr-btn"
                      >
                        <Palette className="w-3 h-3" />
                        Advanced
                      </Button>
                    </div>
                  </div>

                  {/* Logo Toggle */}
                  <div className="flex items-center justify-between w-full p-4 bg-muted/50 rounded-lg">
                    <div>
                      <Label htmlFor="logo-toggle" className="font-medium">Embed Profile Photo as Logo</Label>
                      <p className="text-sm text-muted-foreground">Add your profile photo to the center of QR code</p>
                    </div>
                    <Switch
                      id="logo-toggle"
                      checked={withLogo}
                      onCheckedChange={handleLogoToggle}
                      disabled={!formData.profile_photo}
                      data-testid="logo-toggle"
                    />
                  </div>
                  
                  {!formData.profile_photo && (
                    <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg w-full text-center">
                      Upload a profile photo in the Details tab to enable logo embedding
                    </p>
                  )}
                  
                  <div className="qr-container p-8" style={{ backgroundColor: formData.qr_bg_color }}>
                    {withLogo && qrWithLogoUrl ? (
                      <img 
                        src={qrWithLogoUrl} 
                        alt="QR Code with Logo" 
                        className="w-[250px] h-[250px]"
                        data-testid="qr-with-logo"
                      />
                    ) : (
                      <QRCodeSVG
                        id="qr-code-svg"
                        value={cardUrl}
                        size={250}
                        fgColor={formData.qr_color}
                        bgColor={formData.qr_bg_color}
                        level="H"
                      />
                    )}
                    {generatingQR && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                        <div className="loading-spinner" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <Button onClick={downloadQR} className="gap-2" data-testid="download-qr-btn">
                      <Download className="w-4 h-4" />
                      Download PNG
                    </Button>
                    <Button variant="outline" onClick={copyLink} className="gap-2" data-testid="copy-link-btn">
                      <Copy className="w-4 h-4" />
                      Copy Link
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Card Link</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Share this link or QR code:</p>
                    <code className="text-sm break-all">{cardUrl}</code>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-medium mb-2">How it works</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                        Someone scans your QR code with their phone camera
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                        They see your digital business card instantly
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                        They can save your contact directly to their phone
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="nfc">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    NFC Card Setup
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20">
                    <div className="nfc-badge mb-4">
                      <Smartphone className="w-4 h-4" />
                      NFC Ready
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Program Your NFC Card</h3>
                    <p className="text-sm text-muted-foreground">
                      Use this URL to program your NFC business card. When someone taps the card with their phone, they'll see your digital card.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>NFC URL</Label>
                    <div className="flex gap-2">
                      <Input value={nfcData?.url || cardUrl} readOnly className="font-mono text-sm" />
                      <Button variant="outline" onClick={copyLink} data-testid="copy-nfc-url-btn">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>How to Program NFC Card</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-4">
                    {nfcData?.instructions?.map((instruction, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="text-sm text-muted-foreground">{instruction}</span>
                      </li>
                    ))}
                  </ol>
                  
                  <Separator className="my-6" />
                  
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-2">Recommended NFC Writer Apps:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>NFC Tools (iOS & Android)</li>
                      <li>TagWriter by NXP (Android)</li>
                      <li>NFC TagWriter (iOS)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="print">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Physical Card Design
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20">
                  <h3 className="font-semibold text-lg mb-2">Create Your Physical NFC Card</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Design and customize the look of your physical business card. Choose from multiple templates, 
                    color schemes, and select which information to display.
                  </p>
                  <Button 
                    onClick={() => navigate(`/cards/${cardId}/print`)} 
                    className="gap-2"
                    data-testid="open-print-preview-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Print Preview Designer
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-primary font-bold">1</span>
                    </div>
                    <h4 className="font-medium text-sm">Choose Layout</h4>
                    <p className="text-xs text-muted-foreground mt-1">Horizontal or vertical card orientation</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-primary font-bold">2</span>
                    </div>
                    <h4 className="font-medium text-sm">Select Design</h4>
                    <p className="text-xs text-muted-foreground mt-1">Pick a template and color scheme</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-primary font-bold">3</span>
                    </div>
                    <h4 className="font-medium text-sm">Print or Download</h4>
                    <p className="text-xs text-muted-foreground mt-1">Export your card design</p>
                  </div>
                </div>

                <Separator />

                {/* Order Physical Cards */}
                <div className="p-6 bg-gradient-to-br from-[#16335E]/10 to-[#00A78B]/10 rounded-xl border border-[#16335E]/20">
                  <h3 className="font-semibold text-lg mb-2">Order Physical NFC Cards</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Get professionally printed NFC-enabled business cards delivered to your door.
                  </p>
                  <Button 
                    onClick={() => navigate(`/cards/${cardId}/order`)} 
                    className="gap-2 bg-[#16335E] hover:bg-[#0A1A34]"
                    data-testid="order-cards-btn"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Order Physical Cards
                  </Button>
                </div>

                {/* View History */}
                <div className="p-6 bg-muted/50 rounded-xl border">
                  <h3 className="font-semibold text-lg mb-2">Card View History</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    See who viewed your card including device type, browser, and location estimates.
                  </p>
                  <Button 
                    variant="outline"
                    onClick={() => navigate(`/cards/${cardId}/history`)} 
                    className="gap-2"
                    data-testid="view-history-btn"
                  >
                    <Eye className="w-4 h-4" />
                    View Scan History
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default EditCardPage;
